/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
*  Copyright 2024 Adobe
*  All Rights Reserved.
*
* NOTICE:  All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/
import SafeStorage from '../core/SafeStorage';

function hasStorageMethods(classInstance) {
  if (!classInstance?.constructor?.prototype) {
    return false;
  }
  const storageMethods = Object.getOwnPropertyNames(Storage.prototype);
  const classMethods = Object.getOwnPropertyNames(classInstance.constructor.prototype);
  return storageMethods.every(method => classMethods.includes(method));
}

function initializeStorage() {
  window.adobe_dc_sdk = window.adobe_dc_sdk || {};

  if (typeof jest !== 'undefined' && window.adobe_dc_sdk.safeSessionStorage && window.adobe_dc_sdk.safeLocalStorage) {
    return;
  }

  let sessionStorageSource;
  let localStorageSource;
  try {
    sessionStorageSource = window.sessionStorage;
    localStorageSource = window.localStorage;
    // eslint-disable-next-line no-empty
  } catch (e) {}
  const safeSessionStorage = new SafeStorage(sessionStorageSource);
  const safeLocalStorage = new SafeStorage(localStorageSource);

  Object.defineProperty(window.adobe_dc_sdk, 'safeSessionStorage', {
    value: safeSessionStorage,
    writable: false,
  });

  Object.defineProperty(window.adobe_dc_sdk, 'safeLocalStorage', {
    value: safeLocalStorage,
    writable: false,
  });

  Object.defineProperty(window.adobe_dc_sdk, 'setSafeSessionStorage', {
    value(source) {
      if (!hasStorageMethods(source)) {
        throw new Error('Provided source does not contain all required methods');
      }
      safeSessionStorage.source = source;
    },
    writable: false,
  });

  Object.defineProperty(window.adobe_dc_sdk, 'setSafeLocalStorage', {
    value(source) {
      if (!hasStorageMethods(source)) {
        throw new Error('Provided source does not contain all required methods');
      }
      safeLocalStorage.source = source;
    },
    writable: false,
  });
}

export { hasStorageMethods, initializeStorage };
